import { useState } from 'react';
import Modal from '../shared/Modal';
import Btn from '../shared/Btn';
import Field from '../shared/Field';
import Textarea from '../shared/Textarea';
import { createLMNLetter, generateLMNLetter } from '../../../api/claimsClient';
import LMNEditorPanel from './LMNEditorPanel';
import { useLMNLetterPolling } from '../../../hooks/useLMNLetterPolling';

/**
 * Two-phase modal:
 *   - intake: when letterId is null, show the clinical-notes textarea + Start.
 *   - editor: when letterId is provided, drive the editor via useLMNLetterPolling.
 *
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   claimId: string,
 *   letterId?: string|null,
 *   defaultPrompt?: string,
 *   onJobStarted: (letterId: string) => void,
 *   onRegenerateStarted: (letterId: string) => void,
 *   onDeleteLMN?: (letterId: string) => void,
 * }} props
 */
export default function LMNModal({
  open, onClose,
  claimId,
  letterId = null,
  defaultPrompt = '',
  onJobStarted,
  onRegenerateStarted,
  onDeleteLMN,
}) {
  const [restartKey, setRestartKey] = useState(0);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const [editorDirty, setEditorDirty] = useState(false);
  const polling = useLMNLetterPolling(letterId, claimId, restartKey);

  const inEditor = !!letterId;

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const letter = await createLMNLetter(claimId);
      const body = prompt.trim() ? { clinical_notes: prompt } : {};
      await generateLMNLetter(letter.id, body);
      // Do NOT close: the parent sets activeLetterId from onJobStarted so this
      // modal stays open and transitions to its editor phase (inEditor = !!letterId).
      onJobStarted(letter.id);
    } catch (/** @type {any} */ e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to start LMN generation');
    } finally {
      setBusy(false);
    }
  };

  const requestClose = () => {
    if (inEditor && editorDirty && !window.confirm('Discard unsaved letter edits?')) return;
    setEditorDirty(false);
    onClose();
  };

  const handleRegenerateStarted = (id) => {
    setRestartKey((k) => k + 1);
    setEditorDirty(false);
    onRegenerateStarted?.(id);
  };

  return (
    <Modal
      open={open}
      onClose={requestClose}
      title="Letter of Medical Necessity"
      width={inEditor ? 880 : 640}
      footer={
        inEditor ? (
          <Btn kind="ghost" onClick={requestClose}>Cancel</Btn>
        ) : (
          <>
            <Btn kind="ghost" onClick={onClose} disabled={busy}>Cancel</Btn>
            <Btn kind="primary" icon="sparkles" onClick={start} disabled={busy}>
              {busy ? 'Starting…' : 'Start LMN generation'}
            </Btn>
          </>
        )
      }
    >
      {inEditor ? (
        <LMNEditorPanel
          letter={polling.letter || {
            id: letterId,
            status: polling.status,
            generated_content: '',
            error_message: '',
          }}
          onRegenerateStarted={handleRegenerateStarted}
          onDirtyChange={setEditorDirty}
          onDeleteLMN={onDeleteLMN}
        />
      ) : (
        <>
          <p style={{ fontSize: 13, color: 'var(--ab-fg-subtle)', marginBottom: 12 }}>
            We'll generate the letter in the background. You can keep working — we'll
            attach it automatically when it's ready (~30–60s).
          </p>
          <Field label="Indication / clinical justification (optional)" error={error}>
            <Textarea
              rows={8}
              value={prompt}
              onChange={(/** @type {any} */ e) => setPrompt(e.target.value)}
              placeholder="Why does this patient require this treatment? Describe the medical history, prior treatments tried, and why the current treatment is medically necessary. Leave blank to let the AI infer from the claim."
            />
          </Field>
          <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)', marginTop: 4 }}>
            {prompt.trim().length} character{prompt.trim().length === 1 ? '' : 's'}
          </div>
        </>
      )}
    </Modal>
  );
}
